import { Box, Button, Tooltip, TooltipShortcut } from "prism-react";
import { useRef } from "react";

import { useAnalytics } from "hooks/useAnalytics";
import { KEY_SYMBOLS_MAP } from "utils/keyboard";

interface Props {
  id: string;
  onClose(): void;
  onOpen(): void;
  /** URL to open in popup window. Must be same-origin. */
  popupUrl?: string;
  /** URL to open in new tab. Can be cross-origin */
  url: string;
}

export const IsolateDevtoolButton: React.FC<Props> = ({
  id,
  onClose,
  onOpen,
  popupUrl,
  url,
}) => {
  const popupWindowRef = useRef<Window | null>(null);
  const track = useAnalytics();

  return (
    <Tooltip
      content={
        <Box>
          Open in a new tab, or use
          <br />
          <Box css={{ display: "inline-flex", gap: "$1" }}>
            <TooltipShortcut>{KEY_SYMBOLS_MAP["$mod"]}</TooltipShortcut>
            <TooltipShortcut>click</TooltipShortcut>
          </Box>{" "}
          to open in a popup.
        </Box>
      }
    >
      {/* Weirdly, DevtoolButton doesn't render well as an anchor, but this works */}
      <Button
        as="a"
        href={url}
        icon="window"
        onClick={(e) => {
          if (e.metaKey === false) {
            track("open_preview_window", { event_source: "editor" });
            return;
          }
          e.preventDefault();

          onOpen();

          const el = e.currentTarget as HTMLButtonElement;

          // Distances from this button, to the edge of the window
          const box = el.getBoundingClientRect();

          const { clientHeight, clientWidth } = window.document.body;

          // Size of isolated devtool
          const width = clientWidth / 2;
          const height = clientHeight / 2;

          // Position on screen of isolated devtool
          const left = window.screenLeft + box.left;
          const top = window.screenTop + box.top;

          // If a popup devtool is already open, focus it instead of reloading
          if (popupWindowRef.current) {
            popupWindowRef.current.focus();
            return;
          }

          const devtoolWindow = window.open(
            popupUrl || url,
            id,
            `resizable=yes,width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no`,
          );

          track("open_preview_popup", { event_source: "editor" });

          if (devtoolWindow) {
            popupWindowRef.current = devtoolWindow;

            devtoolWindow.onbeforeunload = () => {
              popupWindowRef.current = null;

              // TODO: this breaks if the sidekick item is moved in the stack
              // between opening and closing. Change the workspace logic to use an
              // ID instead of an index.
              onClose();
            };
          }
        }}
        target="_blank"
      />
    </Tooltip>
  );
};
